<!-- Freight Manager Listing -->
<div class="card">
  <!-- Card Header with Tabs -->
  <div class="card-header border-0 pt-6">
    <div class="card-title">
      <h3 class="fw-bold m-0">Freight Manager</h3>
    </div>

    <!-- Navigation Tabs -->
    <div class="card-toolbar">
      <ul class="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
        <li class="nav-item">
          <a class="nav-link" [class.active]="activeTab === 'roro'" href="javascript:;" (click)="setActiveTab('roro')">
            RORO
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" [class.active]="activeTab === 'ctn'" href="javascript:;" (click)="setActiveTab('ctn')">
            CTN
          </a>
        </li>
      </ul>
    </div>
  </div>

  <!-- Freight Buffer Form -->
  <div class="card-header border-0 pt-0">
    <div class="card-toolbar w-100">
      <form [formGroup]="freightBufferForm" (ngSubmit)="onFreightBufferSubmit()" novalidate class="d-flex align-items-center gap-3">
        <div class="d-flex align-items-center">
          <label class="form-label fw-bold me-3 mb-0">Freight Buffer:</label>
          <input
            type="number"
            class="form-control form-control-sm"
            formControlName="freight_buffer"
            placeholder="Enter freight buffer"
            style="width: 200px;"
            [class.is-invalid]="freightBufferForm.get('freight_buffer')?.invalid && freightBufferForm.get('freight_buffer')?.touched"
          />
          <div *ngIf="freightBufferForm.get('freight_buffer')?.invalid && freightBufferForm.get('freight_buffer')?.touched" class="invalid-feedback">
            <div *ngIf="freightBufferForm.get('freight_buffer')?.errors?.['required']">Freight buffer is required</div>
          </div>
        </div>
        <button
          type="submit"
          class="btn btn-sm btn-primary"
          [disabled]="isLoadingFreightBuffer">
          <span *ngIf="isLoadingFreightBuffer" class="spinner-border spinner-border-sm align-middle me-2"></span>
          Save Buffer
        </button>
      </form>
    </div>
  </div>

  <!-- Card Toolbar -->
  <div class="card-header border-0 pt-0">
    <div class="card-toolbar w-100">
      <!-- Base Toolbar -->
      <div class="d-flex justify-content-between w-100" data-kt-freight-manager-table-toolbar="base">
        <div class="d-flex align-items-center">
          <!-- Filter Button -->
          <button type="button" class="btn btn-sm btn-light-primary me-3" (click)="toggleFilters()">
            <i class="ki-duotone ki-filter fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Filter
          </button>
        </div>

        <div class="d-flex align-items-center gap-2">
          <!-- Save Changes Button (shown when there are changes) -->
          <button *ngIf="hasChanges" type="button" class="btn btn-sm btn-success" (click)="onSave()">
            <i class="ki-duotone ki-check fs-2"></i>
            Save Changes ({{ changedRows.length }})
          </button>

          <!-- Month-Year Filter Dropdown -->
          <select
            class="form-select form-select-sm form-select-solid"
            style="width: 180px;"
            [(ngModel)]="selectedMonthYear"
            (change)="onMonthYearChange($event)">
            <option value="">All Months</option>
            <option *ngFor="let option of monthYearOptions" [value]="option.value">
              {{ option.label }}
            </option>
          </select>

          <!-- Copy Data Button -->
          <button type="button" class="btn btn-sm btn-info" (click)="openCopyDataModal()">
            <i class="ki-duotone ki-copy fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Copy Data
          </button>

          <!-- Export CSV Button -->
          <button type="button" class="btn btn-sm btn-warning" (click)="exportCSV()">
            <i class="ki-duotone ki-file-down fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Export CSV
          </button>

          <!-- Export CSV with Buffer Button -->
          <button type="button" class="btn btn-sm btn-secondary" (click)="exportCSVWithBuffer()">
            <i class="ki-duotone ki-file-down fs-2">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
            Export CSV with Buffer
          </button>

          <!-- Add Freight Manager Button -->
          <button type="button" class="btn btn-sm btn-primary" (click)="openAddFreightManagerModal()">
            <i class="ki-duotone ki-plus fs-2"></i>
            Add {{ activeTab === 'roro' ? 'RORO' : 'CTN' }} Freight Manager
          </button>
        </div>
      </div>

      <!-- Selected Toolbar -->
      <div class="d-flex justify-content-end align-items-center d-none"
        data-kt-freight-manager-table-toolbar="selected">
        <div class="fw-bold me-5">
          <span class="me-2" data-kt-freight-manager-table-select="selected_count"></span>Selected
        </div>
        <button type="button" class="btn btn-danger" (click)="bulkDelete()">Delete Selected</button>
      </div>
    </div>
  </div>

  <!-- Filters Form -->
  <div *ngIf="showFiltersForm" [@slideInOut] class="card-body border-bottom">
    <div class="row g-3">
      <div class="col-md-4">
        <label class="form-label">Search</label>
        <input type="text" class="form-control form-control-solid" placeholder="Search freight managers..."
          [(ngModel)]="searchTerm" (keyup.enter)="applyFilters()" />
      </div>
      <div class="col-md-8 d-flex align-items-end">
        <button type="button" class="btn btn-primary me-2" (click)="applyFilters()">Apply Filters</button>
        <button type="button" class="btn btn-secondary" (click)="resetFilters()">Reset</button>
      </div>
    </div>
  </div>

  <!-- Card Body -->
  <div class="card-body py-4">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="d-flex justify-content-center align-items-center py-10">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoading && freightManagers.length === 0" class="d-flex flex-column flex-center py-10">
      <i class="ki-duotone ki-package fs-3x text-gray-500 mb-4">
        <span class="path1"></span>
        <span class="path2"></span>
        <span class="path3"></span>
      </i>
      <div class="text-gray-500 fs-6 fw-semibold">No freight managers found</div>
      <div class="text-gray-400 fs-7">Try adjusting your search or filter criteria</div>
    </div>

    <!-- Data Table -->
    <div *ngIf="!isLoading && freightManagers.length > 0" class="table-responsive">
      <table class="table align-middle table-row-dashed fs-6 gy-5" id="kt_freight_managers_table">
        <thead>
          <tr class="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
            <th class="w-25px text-center">
              <div class="form-check form-check-sm form-check-custom form-check-solid">
                <input class="form-check-input" type="checkbox" [checked]="isAllSelected()"
                  [indeterminate]="isIndeterminate()" (change)="toggleAllSelection($event)">
              </div>
            </th>
            <th class="min-w-125px">To Country</th>
            <th class="min-w-125px">To Port</th>
            <th class="min-w-150px">Booking Agent</th>
            <th class="min-w-100px text-center">
              <!-- <div class="d-flex flex-column">
                <span>Under 20M3</span>
                <span>Over 20M3</span>
              </div> -->
            </th>
            <!-- Dynamic shipping line columns -->
            <ng-container *ngFor="let shippingLine of shippingLines">
              <th class="min-w-100px text-center">{{ shippingLine.name }}</th>
              <th class="min-w-100px text-center">Validity Upto</th>
            </ng-container>
            <th class="min-w-100px text-center">C.Rate</th>
            <th class="min-w-100px text-center">F.Rate</th>
            <th class="min-w-200px">Memo</th>
            <th class="text-end min-w-100px">Actions</th>
          </tr>
        </thead>
        <tbody class="text-gray-600 fw-semibold">
          <tr *ngFor="let freightManager of freightManagers; trackBy: trackByFn">
            <td class="text-center pe-0">
              <div class="form-check form-check-sm form-check-custom form-check-solid">
                <input class="form-check-input" type="checkbox" [value]="freightManager._id"
                  [checked]="isSelected(freightManager._id)" (change)="toggleSelection(freightManager._id, $event)">
              </div>
            </td>
            <td>
              <span class="text-dark fw-bold fs-6">{{ freightManager.to_country?.name || 'N/A' }}</span>
            </td>
            <td>
              <span class="text-dark fw-bold fs-6">{{ freightManager.to_port?.name || 'N/A' }}</span>
            </td>
            <td>
              <select
                class="form-select form-select-sm form-select-solid"
                [value]="formData[freightManager._id]?.booking_agent_id || ''"
                (change)="onFieldChange(freightManager._id, 'booking_agent_id', $any($event.target).value)"
                [disabled]="isLoadingBookingAgents">
                <option value="">Select Booking Agent</option>
                <option *ngFor="let agent of bookingAgents" [value]="agent._id" [selected]="formData[freightManager._id]?.booking_agent_id === agent._id">
                  {{ agent.name }}
                </option>
              </select>
            </td>
            <td class="text-center">
              <div class="d-flex flex-column gap-2">
                <span class="text-dark fs-7">Under 20M3</span>
                <span class="text-dark fs-7">Over 20M3</span>
              </div>
            </td>
            <!-- Dynamic shipping line columns -->
            <ng-container *ngFor="let shippingLine of shippingLines">
              <!-- Shipping line data column -->
              <td class="text-center">
                <div class="d-flex flex-column gap-2">
                  <!-- Under 20M3 input -->
                  <input type="text" class="form-control form-control-sm" placeholder="Under 20M3"
                    [value]="formData[freightManager._id]?.shipping_lines[shippingLine._id]?.under_20m3 || ''"
                    (input)="onFieldChange(freightManager._id, 'under_20m3', getInputValue($event), shippingLine._id)">

                  <!-- Over 20M3 input -->
                  <input type="text" class="form-control form-control-sm" placeholder="Over 20M3"
                    [value]="formData[freightManager._id]?.shipping_lines[shippingLine._id]?.over_20m3 || ''"
                    (input)="onFieldChange(freightManager._id, 'over_20m3', getInputValue($event), shippingLine._id)">
                </div>
              </td>

              <!-- Validity Upto column -->
              <td class="text-center">
                <input type="date" class="form-control form-control-sm" placeholder="Validity Upto"
                  [value]="formData[freightManager._id]?.shipping_lines[shippingLine._id]?.validity_upto || ''"
                  (input)="onFieldChange(freightManager._id, 'validity_upto', getInputValue($event), shippingLine._id)">
              </td>
            </ng-container>
            <td class="text-center">
              <div class="d-flex flex-column gap-2">
                <span class="text-dark fs-7">{{ getCRate(freightManager, 'under_20m3') }}</span>
                <span class="text-dark fs-7">{{ getCRate(freightManager, 'over_20m3') }}</span>
              </div>
            </td>
            <td class="text-center">
              <div class="d-flex flex-column gap-2">
                <span class="text-dark fs-7">{{ getFRate(freightManager, 'under_20m3') }}</span>
                <span class="text-dark fs-7">{{ getFRate(freightManager, 'over_20m3') }}</span>
              </div>
            </td>
            <td>
              <span class="text-dark fs-6">{{ freightManager.memo || 'N/A' }}</span>
            </td>
            <td class="text-end">
              <div class="d-flex justify-content-end gap-2">
                <!-- Edit Icon -->
                <button type="button" class="btn btn-sm btn-icon btn-light-primary"
                  (click)="openEditModal(freightManager)" title="Edit Freight Manager">
                  <i class="ki-duotone ki-pencil fs-5">
                    <span class="path1"></span>
                    <span class="path2"></span>
                  </i>
                </button>

                <!-- Delete Icon -->
                <button type="button" class="btn btn-sm btn-icon btn-light-danger"
                  (click)="deleteFreightManager(freightManager)" title="Delete Freight Manager">
                  <i class="ki-duotone ki-trash fs-5">
                    <span class="path1"></span>
                    <span class="path2"></span>
                    <span class="path3"></span>
                    <span class="path4"></span>
                    <span class="path5"></span>
                  </i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="!isLoading && freightManagers.length > 0">
      <app-pagination [paginationInfo]="paginationInfo" [loading]="isLoading" (pageChange)="onPageChange($event)">
      </app-pagination>
    </div>
  </div>
</div>

<!-- Freight Manager Add/Edit Modal -->
<app-add-edit-freight-manager-new [selectedType]="activeTab" (freightManagerAdded)="onFreightManagerAdded()"
  (freightManagerUpdated)="onFreightManagerUpdated()">
</app-add-edit-freight-manager-new>

<!-- Copy Freight Manager Modal -->
<app-copy-freight-manager (dataCopied)="onDataCopied()">
</app-copy-freight-manager>
